// Synthetic intrabar ticks constrained to historical OHLC, not recorded trades.
export const PATH_STEPS = 48;
// Regular hourly marks, zero risk-free rate/MAR; undefined ratios stay null.
export function performanceMetrics(equity, benchmarkEquity) {
  const returns = equity.slice(1).map((v, i) => v / equity[i] - 1);
  const mean = returns.reduce((a, b) => a + b, 0) / (returns.length || 1);
  const variance = returns.reduce((a, r) => a + (r - mean) ** 2, 0) / (returns.length || 1);
  const downside = returns.reduce((a, r) => a + Math.min(r, 0) ** 2, 0) / (returns.length || 1);
  let peak = equity[0], drawdown = 0;
  for (const value of equity) { peak = Math.max(peak, value); drawdown = Math.max(drawdown, 1 - value / peak); }
  const returnPct = (equity.at(-1) / equity[0] - 1) * 100;
  const buyHoldPct = (benchmarkEquity / equity[0] - 1) * 100;
  return { returnPct, buyHoldPct, excessPct: returnPct - buyHoldPct, maxDrawdownPct: drawdown * 100,
    sharpe: returns.length > 1 && variance > 1e-20 ? mean / Math.sqrt(variance) * Math.sqrt(8760) : null,
    sortino: returns.length > 1 && downside > 1e-20 ? mean / Math.sqrt(downside) * Math.sqrt(8760) : null };
}
export function candlePath(c, random = Math.random) {
  const first = 10 + Math.floor(random() * 7), second = 29 + Math.floor(random() * 9);
  const highFirst = random() < .5;
  const anchors = [[0, c.open], [first, highFirst ? c.high : c.low], [second, highFirst ? c.low : c.high], [PATH_STEPS, c.close]];
  const path = [c.open];
  // Correlated velocity carries momentum. A short impulse changes its pace;
  // the bridge correction pins each leg to its historical endpoint.
  let velocity = (random() - .5) * .4;
  for (let a = 1; a < anchors.length; a++) {
    const [start, from] = anchors[a - 1], [end, to] = anchors[a], length = end - start;
    const impulse = 2 + Math.floor(random() * Math.max(1, length - 4));
    const force = (random() - .5) * 2, track = [0];
    let volatility = .15 + random() * .3;
    for (let i = 1; i <= length; i++) {
      volatility = volatility * .92 + (.08 + random() * .4) * .08;
      velocity = velocity * .84 + (random() - .5) * volatility + (i === impulse ? force : 0);
      track.push(track.at(-1) + velocity);
    }
    for (let i = 1; i <= length; i++) {
      const t = i / length, eased = t * t * (3 - 2 * t);
      const base = from + (to - from) * eased;
      const bridge = track[i] - t * track[length];
      const room = Math.max(0, Math.min(base - c.low, c.high - base));
      path.push(i === length ? to : base + Math.tanh(bridge) * room * .65 * Math.sin(Math.PI * t));
    }
  }
  return path;
}

// Small causal replay model, not a reconstruction of live-issued predictions.
// Uses only closed/revealed prices, with damped momentum and widening bands.
export function replayForecast(candles) {
  const recent = candles.slice(-25), last = recent.at(-1);
  if (!last || recent.length < 2) return [];
  const returns = recent.slice(1).map((c, i) => Math.log(c.close / recent[i].close));
  const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
  const sigma = Math.max(.001, Math.sqrt(returns.reduce((sum, r) => sum + (r - mean) ** 2, 0) / returns.length));
  const trend = returns.slice(-6).reduce((sum, r, i) => sum + r * (i + 1), 0) / (Math.min(6, returns.length) * (Math.min(6, returns.length) + 1) / 2);
  const drift = Math.max(-2 * sigma, Math.min(2 * sigma, trend * .6 + mean * .4));
  let offset = 0;
  return Array.from({ length: 3 }, (_, i) => {
    const hour = i + 1; offset += drift * Math.exp(-i * .4);
    const close = last.close * Math.exp(offset), spread = Math.min(.5, sigma * Math.sqrt(hour) * 1.28);
    return { timestamp: last.timestamp + hour * 3600, close, low_band: close * Math.exp(-spread), high_band: close * Math.exp(spread) };
  });
}

// Long/cash, full-equity arcade accounting at the currently displayed price.
export class TradingRound {
  constructor(data) {
    if (!data || ![1, 2, 4].includes(data.markets?.length) || data.warmup !== 36 || data.bars !== 120 || ![500, 550].includes(data.interval_ms)) throw Error('Invalid round data. Please try again.');
    for (const market of data.markets) {
      if (!/^[A-Z0-9]{1,12}$/.test(market.symbol) || market.candles?.length !== data.warmup + data.bars) throw Error('Incomplete historical round.');
      market.candles.forEach((c, i) => {
        if (![c.open, c.high, c.low, c.close, c.timestamp].every(v => Number.isFinite(v) && v > 0) || c.high < Math.max(c.open, c.close) || c.low > Math.min(c.open, c.close) || (i && c.timestamp - market.candles[i - 1].timestamp !== 3600)) throw Error('Invalid hourly history.');
      });
    }
    this.data = data;
    this.index = data.warmup - 1;
    this.cash = 10000;
    this.position = null;
    this.selected = 0;
    this.state = 'ready';
    this.trades = [];
    this.journey = [10000];
    this.progress = 0;
    this.live = null;
  }
  get elapsed() { return this.index - this.data.warmup + 1; }
  get performance() {
    // Fixed equal initial allocations; no rebalancing or future prices.
    const benchmark = 10000 * this.data.markets.reduce((sum, m, i) => sum + this.price(i) / m.candles[this.data.warmup - 1].close, 0) / this.data.markets.length;
    return performanceMetrics(this.journey, benchmark);
  }
  forecast(market = this.selected) {
    if (!this.data.easy) return [];
    const now = this.data.markets[market].candles[this.index].timestamp;
    this.forecastSource = 'archived';
    const candidates = (this.data.markets[market].forecasts || []).filter(f => Number.isFinite(f.base) && Number.isFinite(f.issued) && f.base <= now && f.issued <= now + 3600 && f.issued >= f.base && Array.isArray(f.points));
    candidates.sort((a, b) => b.base - a.base);
    for (const f of candidates) {
      const points = f.points.filter(p => Number.isFinite(p.timestamp) && p.timestamp > now && p.timestamp <= now + (this.data.super_easy ? 168 : 3) * 3600 && Number.isFinite(p.close) && p.close > 0).sort((a,b) => a.timestamp - b.timestamp);
      if (points.length) return points;
    }
    if (!this.data.super_easy) {
      const history = this.data.markets[market].candles.slice(Math.max(0, this.index - 24), this.index + 1);
      if (this.live) history.push(this.live[market]);
      this.forecastSource = 'replay';
      return replayForecast(history);
    }
    return [];
  }
  price(market = this.selected) { return this.live?.[market].close ?? this.data.markets[market].candles[this.index].close; }
  get displayIndex() { return this.index + (this.live ? 1 : 0); }
  advance(milliseconds) {
    if (this.state !== 'running' || !Number.isFinite(milliseconds) || milliseconds <= 0) return;
    let remaining = milliseconds;
    while (remaining > 0 && this.state === 'running') {
      if (!this.live) {
        this.paths = this.data.markets.map(m => candlePath(m.candles[this.index + 1]));
        this.live = this.data.markets.map(m => {
          const c = m.candles[this.index + 1];
          return { timestamp: c.timestamp, open: c.open, high: c.open, low: c.open, close: c.open };
        });
      }
      const used = Math.min(remaining, this.data.interval_ms - this.progress);
      const before = this.progress / this.data.interval_ms * PATH_STEPS;
      this.progress += used;
      const after = this.progress / this.data.interval_ms * PATH_STEPS;
      this.live.forEach((c, m) => {
        const path = this.paths[m], i = Math.min(PATH_STEPS - 1, Math.floor(after));
        c.close = path[i] + (path[i + 1] - path[i]) * (after - i);
        for (let j = Math.floor(before) + 1; j <= Math.floor(after); j++) {
          c.high = Math.max(c.high, path[j]); c.low = Math.min(c.low, path[j]);
        }
        c.high = Math.max(c.high, c.close); c.low = Math.min(c.low, c.close);
      });
      remaining -= used;
      if (this.progress >= this.data.interval_ms) this.tick();
    }
  }
  get equity() { return this.position ? this.position.units * this.price(this.position.market) : this.cash; }
  start() { if (this.state === 'ready' || this.state === 'paused') this.state = 'running'; }
  buy(owner) {
    if (this.state !== 'running' || this.position) return false;
    this.position = { market: this.selected, entry: this.price(), units: this.cash / this.price(), cost: this.cash, index: this.displayIndex, entryFraction: this.live ? this.progress / this.data.interval_ms : 1, owner };
    this.cash = 0;
    return true;
  }
  sell(owner, force = false) {
    if (!this.position || (!force && this.position.owner !== owner)) return false;
    const p = this.position;
    this.cash = p.units * this.price(p.market);
    this.trades.push({ ...p, exit: this.price(p.market), end: this.displayIndex, endFraction: this.live ? this.progress / this.data.interval_ms : 1, pnl: this.cash - p.cost });
    this.position = null;
    return true;
  }
  select(index) {
    if (!this.position && index >= 0 && index < this.data.markets.length) this.selected = index;
  }
  tick() {
    if (this.state !== 'running') return;
    this.index++;
    this.live = null;
    this.progress = 0;
    this.journey.push(this.equity);
    if (this.elapsed >= this.data.bars) {
      this.sell(null, true);
      this.state = 'finished';
    }
  }
  pause() {
    if (this.state !== 'running') return;
    this.sell(null, true);
    this.state = 'paused';
  }
}

// Practice habit labels. Each one is a description of observable play in this
// arcade round, never a claim about real trading skill.
export const HABITS = {
  observer: 'Flat for all 120 bars. Nothing to grade yet — next round take one position and feel what holding costs you in nerve.',
  reactive: 'Twenty or more entries in 120 bars. Try a round with fewer trades and compare the results.',
  patient: 'Few entries, and at least half closed above entry. Try the same plan in another round.',
  cutter: 'Your worst trade cost more than your best trade made. Set an exit rule before your next entry and review what happens.',
  cautious: 'You were flat most of the round. Sitting out is a decision — make it one you chose rather than drifted into.',
  active: 'Most of the round was in the market on a mixed tape. Pick one exit rule before the next round and see if the result moves.'
};
export function habit(review) {
  if (!review.trades.length) return 'observer';
  if (review.trades.length >= 20) return 'reactive';
  if (review.best?.pnl > 0 && review.worst.pnl < -3 * review.best.pnl) return 'cutter';
  if (review.winRatePct >= 50 && review.trades.length <= 10) return 'patient';
  if (review.timeInMarketPct < 15) return 'cautious';
  return 'active';
}
// Post-round evidence, computed only from this round's own trades and the
// candles the player had already revealed. No future prices, no hindsight path.
export function reviewRound(round) {
  const base = round.data.warmup - 1, bars = round.data.bars;
  const trades = round.trades.map((t, i) => {
    const start = t.index - base + t.entryFraction, end = t.end - base + t.endFraction;
    return { n: i + 1, market: t.market, entry: t.entry, exit: t.exit, pnl: t.pnl,
      pct: t.cost > 0 ? t.pnl / t.cost * 100 : 0, start, end, holdBars: Math.max(0, end - start) };
  });
  // A bar is "flat" when no position in that market covered any part of it.
  const flat = round.data.markets.map(() => new Array(bars).fill(true));
  for (const t of trades) for (let k = 0; k < bars; k++) if (t.start < k + 1 && t.end > k) flat[t.market][k] = false;
  const revealed = Math.max(0, Math.min(bars, round.index - base));
  let biggest = null, missed = null;
  round.data.markets.forEach((market, m) => {
    for (let k = 0; k < revealed; k++) {
      const pct = (market.candles[base + k + 1].close / market.candles[base + k].close - 1) * 100;
      if (!biggest || Math.abs(pct) > Math.abs(biggest.pct)) biggest = { market: m, bar: k + 1, pct };
      if (flat[m][k] && (!missed || Math.abs(pct) > Math.abs(missed.pct))) missed = { market: m, bar: k + 1, pct };
    }
  });
  const wins = trades.filter(t => t.pnl > 0).length;
  const holdBars = trades.reduce((sum, t) => sum + t.holdBars, 0);
  const stats = round.performance;
  const review = { trades, wins, losses: trades.length - wins, winRatePct: trades.length ? wins / trades.length * 100 : 0,
    holdBars, timeInMarketPct: holdBars / bars * 100, biggest, missed,
    best: trades.reduce((a, t) => (!a || t.pnl > a.pnl ? t : a), null),
    worst: trades.reduce((a, t) => (!a || t.pnl < a.pnl ? t : a), null),
    returnPct: stats.returnPct, buyHoldPct: stats.buyHoldPct, excessPct: stats.excessPct, maxDrawdownPct: stats.maxDrawdownPct,
    sharpe: stats.sharpe, sortino: stats.sortino };
  review.habit = habit(review);
  return review;
}
// Repeat-play targets set by the player against their own play-money record.
export function commitmentOptions(bestPct) {
  const known = Number.isFinite(bestPct);
  const targetPct = known ? Math.max(1, Math.ceil(bestPct + 1)) : 1;
  return [
    { id: 'beat-best', targetPct, label: `Beat +${targetPct.toFixed(2)}%`, hint: known ? 'One step past your personal best in this mode.' : 'No personal best here yet. Set the bar low and set it first.' },
    { id: 'patient', maxTrades: 3, label: 'Trade 3 times or fewer', hint: 'Practice waiting. Fewer decisions, better ones.' },
    { id: 'beat-hold', label: 'Beat buy & hold', hint: 'Finish above the equal-weight basket over the same 120 bars.' }
  ];
}
export function gradeCommitment(commitment, review) {
  if (!commitment) return null;
  if (commitment.id === 'beat-best') return { id: 'beat-best', kept: review.returnPct >= commitment.targetPct, target: commitment.targetPct, actual: review.returnPct };
  if (commitment.id === 'patient') return { id: 'patient', kept: review.trades.length <= commitment.maxTrades, target: commitment.maxTrades, actual: review.trades.length };
  return { id: 'beat-hold', kept: review.excessPct > 0, target: 0, actual: review.excessPct };
}
